import { useRef, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Upload, Trash2, Film, Loader2 } from "lucide-react";
import { uploadChunks } from "@/utils/chunkedUpload";

const MAX_BYTES = 1024 * 1024 * 1024;
const TYPES = ["video/mp4", "video/quicktime", "video/webm"];
const mb = (n) => `${(n / (1024 * 1024)).toFixed(n > 100 * 1024 * 1024 ? 0 : 1)} MB`;

export const VideoUploader = ({ lessonId, form, setForm }) => {
  const inputRef = useRef(null);
  const [pct, setPct] = useState(null);
  const [stage, setStage] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!TYPES.includes(file.type)) { toast.error("Please choose an MP4, MOV or WebM video"); return; }
    if (file.size > MAX_BYTES) { toast.error("Video must be 1 GB or smaller"); return; }
    try {
      setPct(0); setStage("Uploading");
      const { data: init } = await api.post(`/lms/admin/lessons/${lessonId}/video/init`, { filename: file.name, size: file.size, content_type: file.type });
      await uploadChunks(init.upload_id, file, init.chunk_size, setPct);
      setStage("Finalising");
      const { data: lesson } = await api.post(`/lms/admin/video-uploads/${init.upload_id}/complete`);
      setForm((f) => ({ ...f, video_storage_path: lesson.video_storage_path, video_filename: lesson.video_filename }));
      toast.success("Video uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setPct(null); setStage(""); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the uploaded video from this lesson?")) return;
    try { await api.delete(`/lms/admin/lessons/${lessonId}/video`); setForm((f) => ({ ...f, video_storage_path: "", video_filename: "" })); toast.success("Video removed"); }
    catch (err) { toast.error("Failed to remove video"); }
  };

  if (!lessonId) return <p className="text-xs text-muted-foreground italic" data-testid="video-upload-hint">Save the lesson first, then you can upload a video.</p>;

  return (
    <div className="space-y-2" data-testid="video-uploader">
      <Label className="text-xs">Uploaded video (MP4 / MOV / WebM, up to 1 GB)</Label>
      {form.video_storage_path ? (
        <div className="rounded-md border border-border bg-slate-50 p-2 space-y-2">
          <div className="flex items-center gap-2 text-xs">
            <Film size={14} className="text-green-700" />
            <span className="flex-1 truncate" data-testid="video-filename">{form.video_filename || form.video_storage_path.split("/").pop()}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200 gap-1" onClick={remove} data-testid="video-remove"><Trash2 size={11} /> Remove</Button>
          </div>
          <video controls preload="metadata" src={`${process.env.REACT_APP_BACKEND_URL}/api/lms/admin/lessons/${lessonId}/video`} className="w-full max-h-56 rounded bg-black" data-testid="video-preview" />
        </div>
      ) : pct === null && (
        <Button type="button" size="sm" variant="outline" className="gap-1 text-xs" onClick={() => inputRef.current?.click()} data-testid="video-upload-btn"><Upload size={12} /> Upload video</Button>
      )}
      {pct !== null && (
        <div className="space-y-1" data-testid="video-upload-progress">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground"><span className="flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> {stage}…</span><span>{pct}%</span></div>
          <Progress value={pct} className="h-1.5" />
        </div>
      )}
      <input ref={inputRef} type="file" accept={TYPES.join(",")} className="hidden" onChange={onPick} data-testid="video-file-input" />
      {form.video_storage_path && form.video_external_url && <p className="text-[11px] text-amber-700">Both a link and an upload are set — learners will see the link.</p>}
      <p className="text-[11px] text-muted-foreground">Stored in the LMS's own storage — never shared with the CRM. Large files: {mb(MAX_BYTES)} max.</p>
    </div>
  );
};
